import React, { useState, useEffect } from "react";
import "./littleBox.style.scss";

import { useDataLayer } from "../../../Contexts/DataLayer.context.jsx";
import shareIcon from "../../../assets/icons/share.svg";
import { getPlayersFromList, saveToDB, saveSharedToDB, updateTheLog } from "../../../utils/functions.ts";
import { usePlayer } from "../../../Contexts/playerData.context.jsx";
import notShare from "../../../assets/icons/plusFBX.svg";
import { useNavBar } from "../../../Contexts/navbar.items.context.jsx";
import ScoutBox from "./ScoutBox/scoutbox.component.jsx";
import { jwtDecode } from "jwt-decode";
import RNFBox from "../../Universals/RNFBox/RNFBox.component.jsx";
import LittleBoxLog from "./littleBoxLog.component.jsx";
import EditBox from "./editBox.component.jsx";

// ------------------------ module-scope constants ------------------------
const STORAGE_KEY = "littleBoxPosition";
const DEFAULT_POS = { x: 57, y: 175 };
const LOCAL_STORAGE_LIST = "FBX_SELECTED_LIST";
const RESET_WINDOW_POSITION_EVENT = "FBX_RESET_WINDOW_POSITION";

const loadSavedPosition = () => {
  try {
    if (typeof window === "undefined") return DEFAULT_POS;
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_POS;
  } catch {
    return DEFAULT_POS;
  }
};

const LittleBox = () => {
  // Pull everything from player context (including listLoadID)
  const {
    playerID = "",
    playerData,
    setPlayerData,
    setListLoadID,
    setSavedOriginalPlayerData,
    setActivePlayer,
    setPlayerID,
  } = usePlayer();

  const { listArray, updateListArray, updateWhichOneLiner, actualShared, arrayOfShared } = useDataLayer();

  const { updateActiveSideButton, updateSearchedListName, updateIsPillSelected } = useNavBar();

  // ✅ Always work with a safe array (prevents crashes when a record becomes undefined)
  const safeListArray = Array.isArray(listArray) ? listArray.filter(Boolean) : [];

  // UI + drag
  const [isExpanded, setIsExpanded] = useState(false);
  const [position, setPosition] = useState(() => loadSavedPosition());
  const [dragging, setDragging] = useState(false);
  const [startOffset, setStartOffset] = useState({ x: 0, y: 0 });

  // sharing/editor state
  const [sharedScoutList, updateSharedScoutList] = useState([]);
  const [activeListIDForShare, updateActiveListIDForShare] = useState(null);
  const [displayNameForShare, updateDisplayNameForShare] = useState(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [theRecordToEdit, updateTheRecordToEdit] = useState([]);
  const [isSpecial, updateIsSpecial] = useState("delete");
  const [showConfirmationBox, setShowConfirmationBox] = useState(false);
  const [showDeleteConfirmationBox, setShowDeleteConfirmationBox] = useState(false);

  // create new list flow
  const [newList, createNewList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [pickFromList, setPickFromList] = useState(false);

  const [activeListID, setActiveListID] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [loadingListId, setLoadingListId] = useState(null);

  useEffect(() => {
    const handleExternalReset = (event) => {
      const key = event?.detail?.storageKey || "";
      if (key !== STORAGE_KEY && key !== "*") return;

      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}

      setPosition(DEFAULT_POS);
    };

    window.addEventListener(RESET_WINDOW_POSITION_EVENT, handleExternalReset);
    return () => window.removeEventListener(RESET_WINDOW_POSITION_EVENT, handleExternalReset);
  }, []);

  // ------------------------ actions ------------------------

  const closeScoutDrawer = () => {
    setIsExpanded(false);
    updateSharedScoutList([]);
    updateActiveListIDForShare(null);
    updateDisplayNameForShare("");
  };

  const handleListLog = () => {
    setShowLogModal(true);
  };

  useEffect(() => {
    const refreshListInfo = async () => {
      try {
        const listIDs = (Array.isArray(listArray) ? listArray : [])
          .filter(Boolean)
          .map((item) => item?.listId)
          .filter(Boolean)
          .map((id) => String(id));

        if (listIDs.length === 0) return;

        const params = new URLSearchParams();
        params.set("method", "getListTotalsAndShared");  // ✅ must match PHP
        params.set("listIDs", JSON.stringify(listIDs));  // ✅ send as JSON string

        const res = await fetch("https://www.fbxchange.com/ReactPHP/listBox.php", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params.toString(),
          // credentials: "include", // uncomment only if your PHP requires cookies/session
        });

        const raw = await res.text(); // ✅ always read text first

        // If server returns HTML or a warning, you’ll see it here:
        if (!res.ok) {
          console.error("refreshListInfo HTTP error:", res.status, raw);
          return;
        }

        let updatedInfoRaw;
        try {
          updatedInfoRaw = JSON.parse(raw);
        } catch (e) {
          console.error("refreshListInfo: response was not JSON:", raw);
          return;
        }

        const updatedInfo = (updatedInfoRaw ?? []).filter(Boolean).map((u) => ({
          listId: String(u.listId ?? ""),
          listTotal: Number(u.listTotal ?? 0),
          sharedToId: String(u.sharedToId ?? u.sharedToID ?? ""),
        }));

        const newListArray = (Array.isArray(listArray) ? listArray : [])
          .filter(Boolean)
          .map((item) => {
            const found = updatedInfo.find((u) => String(u.listId) === String(item?.listId));
            return found
              ? { ...item, listTotal: found.listTotal, sharedToId: found.sharedToId }
              : item;
          });

        updateListArray(newListArray);
      } catch (err) {
        console.error("Failed to refresh list info:", err);
      }
    };

    refreshListInfo();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const savedListId = localStorage.getItem(LOCAL_STORAGE_LIST);
    if (savedListId) setActiveListID(savedListId);
  }, []);

  const activateList = async (AlistID) => {
    const listId = String(AlistID ?? "");
    if (!listId) return;

    setLoadingListId(listId);
    try {
      localStorage.setItem(LOCAL_STORAGE_LIST, listId);
      setActiveListID(listId);
      updateWhichOneLiner(null);

      const token = localStorage.getItem("user2026");
      const theUser = jwtDecode(token);

      const newPlayers = await getPlayersFromList(listId, theUser.team, theUser.scout);

      setPlayerData(newPlayers);
      setSavedOriginalPlayerData(newPlayers);

      setPlayerID(newPlayers[0]?.FBXID || "");
      setActivePlayer(newPlayers[0] || []);
      setListLoadID(Math.floor(Math.random() * (1000000 - 1000 + 1)) + 1000);

      const newActiveList = safeListArray.find((item) => String(item?.listId) === listId);
      if (newActiveList) updateSearchedListName("Current List: " + (newActiveList.listName ?? ""));

      updateIsPillSelected(false);
      setIsExpanded(false);
      updateActiveSideButton(null);
    } catch (e) {
      console.error("activateList failed:", e);
    } finally {
      setLoadingListId(null);
    }
  };

  // Toggle the share drawer
  const handleShareClick = (listID) => {
    const listId = String(listID ?? "");
    if (!listId) return;

    localStorage.setItem(LOCAL_STORAGE_LIST, listId);
    setActiveListID(listId);

    if (activeListIDForShare) {
      if (listId === activeListIDForShare) {
        closeScoutDrawer();
        return;
      } else {
        const oneRecord = safeListArray.find((item) => String(item?.listId) === listId);
        if (oneRecord) {
          updateActiveListIDForShare(listId);
          updateDisplayNameForShare(oneRecord.listName ?? "");
          const numberArray = oneRecord.sharedToId ? String(oneRecord.sharedToId).split(",").map(String) : [];
          updateSharedScoutList(numberArray);
        }
        return;
      }
    }

    setIsExpanded((prev) => {
      const next = !prev;
      if (next) {
        const oneRecord = safeListArray.find((item) => String(item?.listId) === listId);
        if (oneRecord) {
          updateActiveListIDForShare(listId);
          updateDisplayNameForShare(oneRecord.listName ?? "");
          const numberArray = oneRecord.sharedToId ? String(oneRecord.sharedToId).split(",").map(String) : [];
          updateSharedScoutList(numberArray);
        }
      } else {
        closeScoutDrawer();
      }
      return next;
    });
  };

  // ✅ Add/remove player in list (FIXED: no undefined list items)
  const handleAddRemoveClick = (listIdIn, playerIdIn, isPlayerInList) => {
    const listId = String(listIdIn ?? "");
    const pid = String(playerIdIn ?? "");
    if (!listId || !pid) return;

    const updatedListArray = safeListArray
      .map((listItem) => {
        if (String(listItem?.listId ?? "") !== listId) return listItem;

        const theNumbersRaw = Array.isArray(listItem?.theNumbers) ? listItem.theNumbers : [];
        const theNumbers = theNumbersRaw.map(String);

        const nextNumbers = isPlayerInList
          ? theNumbers.filter((n) => n !== pid)
          : Array.from(new Set([...theNumbers, pid]));

        // fire-and-forget save (don’t break UI if it fails)
        try {
          saveToDB(pid, listId);
        } catch (e) {
          console.warn("saveToDB failed:", e);
        }

        return {
          ...listItem,
          theNumbers: nextNumbers,
          listTotal: nextNumbers.length,
        };
      })
      .filter(Boolean);

    updateListArray(updatedListArray);
  };

  // Drag start/move/up (save final to localStorage)
  const handleMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setStartOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - startOffset.x, y: e.clientY - startOffset.y });
  };

  const handleMouseUp = (e) => {
    setDragging(false);
    const finalPos = { x: e.clientX - startOffset.x, y: e.clientY - startOffset.y };
    setPosition(finalPos);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(finalPos));
    } catch (err) {
      console.warn("Could not save position:", err);
    }
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    // eslint-disable-next-line
  }, [dragging]);

  // Toggle a scout in share list
  const processWhatScout = (scout) => {
    const scoutId = String(scout ?? "");
    if (!scoutId) return;

    const removeORadd = sharedScoutList.includes(scoutId) ? "Removed" : "Added";
    updateTheLog(removeORadd, scoutId, "Admin", activeListIDForShare);

    const updated = sharedScoutList.includes(scoutId)
      ? sharedScoutList.filter((id) => id !== scoutId)
      : [...sharedScoutList, scoutId];

    updateSharedScoutList(updated);

    const updatedListArray = safeListArray
      .map((listItem) =>
        String(listItem?.listId ?? "") === String(activeListIDForShare ?? "")
          ? { ...listItem, sharedToId: updated.join(",") }
          : listItem
      )
      .filter(Boolean);

    updateListArray(updatedListArray);

    try {
      saveSharedToDB(activeListIDForShare, updated.join(","));
    } catch (e) {
      console.warn("saveSharedToDB failed:", e);
    }
  };

  // Enter edit mode for a list
  const setTheRecordToChange = (listID) => {
    const listId = String(listID ?? "");
    if (!listId) return;

    const rec = safeListArray.find((item) => String(item?.listId ?? "") === listId);
    localStorage.setItem(LOCAL_STORAGE_LIST, listId);
    setActiveListID(listId);
    if (!rec) return;

    if (rec.listName === "-QUICK LIST-" || rec.listName === "-WATCHLIST-") {
      updateIsSpecial("noDelete");
    } else {
      updateIsSpecial("delete");
    }

    createNewList(false);
    setPickFromList(false);

    updateTheRecordToEdit(rec);
    setIsEditMode(true);
    setShowConfirmationBox(false);
    setShowDeleteConfirmationBox(false);
  };

  const AddCurrentList = (type) => {
    const token = localStorage.getItem("user2026");
    const theUser = jwtDecode(token);

    let playersToAdd = [];
    if (type === "full") {
      playersToAdd = [...new Set((playerData || []).map((p) => p.FBXID).filter((id) => id))];
    }

    fetch("https://www.fbxchange.com/ReactPHP/listBox.php", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        method: "createList2025",
        user: JSON.stringify(theUser),
        listName: newListName,
        players: JSON.stringify(playersToAdd),
      }).toString(),
    })
      .then((res) => res.json())
      .then((res) => {
        updateListArray(Array.isArray(res) ? res.filter(Boolean) : []);
        createNewList(false);
        setPickFromList(false);
        setNewListName("");
      })
      .catch((err) => {
        console.error("Failed to create list:", err);
      });
  };

  // ------------------------ render ------------------------
  return (
    <div
      className={`littleBox-outside ${isExpanded ? "expanded" : ""}`}
      style={{
        position: "absolute",
        left: position.x,
        top: position.y,
        cursor: dragging ? "grabbing" : "grab",
      }}
    >
      <div className="insideBox">
        <div className="leftSide">
          <div
            className="card-header d-flex align-items-center p-0 pb-1"
            onMouseDown={handleMouseDown}
            title="Drag"
            style={{ userSelect: "none", gap: "8px" }}
          >
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                try {
                  localStorage.removeItem(STORAGE_KEY);
                } catch { }
                setPosition(DEFAULT_POS);
              }}
            >
              Reset
            </button>

            <div className="flex-grow-1" />

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                onClick={() => {
                  createNewList(!newList);
                  setNewListName("");
                  setPickFromList(false);
                  setIsEditMode(false);
                }}
              >
                Create New List
              </button>

              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                title="Close"
                onClick={() => {
                  updateActiveSideButton(null);
                }}
              >
                X
              </button>
            </div>
          </div>

          <div>
            {newList && !isEditMode ? (
              <>
                <div className="alert alert-light text-center my-2">
                  <strong>Create a New List (Minimum 10 Characters)</strong>
                  <div className="mt-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter List Name"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <button
                    className="btn btn-primary mt-3 w-50"
                    disabled={newListName.trim().length < 10}
                    onClick={() => {
                      const cleanName = newListName.trim();
                      if (!cleanName) return;
                      setPickFromList(true);
                    }}
                  >
                    Next
                  </button>
                  <button
                    className="btn btn-outline-danger mt-3 w-25"
                    style={{ marginLeft: "4px" }}
                    onClick={() => {
                      setNewListName("");
                      createNewList(false);
                      setPickFromList(false);
                    }}
                  >
                    Cancel
                  </button>

                  {pickFromList && (
                    <div className="mt-4 p-3 alert alert-light">
                      <button className="btn btn-outline-primary me-2 p-3 col-23" onClick={() => AddCurrentList("full")}>
                        Current List
                      </button>
                      <button className="btn btn-outline-primary p-3 col-23" onClick={() => AddCurrentList("empty")}>
                        Blank List
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* ---------------- My Lists ---------------- */}
                <div id="myListContent" style={{ display: isEditMode ? "none" : "block" }}>
                  <div className="d-flex p-2 mb-2">
                    <div className="ms-auto">
                      <RNFBox buttonList={arrayOfShared} selectedButtons={actualShared} />
                    </div>
                  </div>

                  {!playerID && (
                    <div className="alert alert-warning p-2 text-center">
                      The Buttons Are Disabled because you do not have a list of players Loaded
                    </div>
                  )}

                  <div>
                    {safeListArray.map((listItem, idx) => {
                      const listId = String(listItem?.listId ?? "");
                      if (!listId) return null;

                      const pid = String(playerID ?? "");
                      const theNumbers = Array.isArray(listItem?.theNumbers) ? listItem.theNumbers : [];
                      const isPlayerInList = pid ? theNumbers.map(String).includes(pid) : false;

                      return (
                        <div
                          className={`listItem  ${listId === String(activeListID ?? "") ? "alert-info" : ""}`}
                          key={listId || idx}
                        >
                          <div
                            className="editBox"
                            title="Edit List"
                            onClick={() => {
                              if (isExpanded) handleShareClick(activeListIDForShare);
                              setTheRecordToChange(listId);
                            }}
                          >
                            <div className="btn btn-sm btn-outline-secondary fs-8">Edit</div>
                          </div>

                          <div
                            className={`listName ${loadingListId === listId ? "fw-bold" : ""}`}
                            title="List Name"
                            onClick={() => activateList(listId)}
                            aria-busy={loadingListId === listId ? "true" : "false"}
                          >
                            {loadingListId === listId ? "Loading…" : (listItem?.listName ?? "")}
                          </div>

                          <div className="counter">{Number(listItem?.listTotal ?? 0)}</div>

                          <div className="buttons">
                            {pid ? (
                              <button
                                className={`btn btn-sm text-white col-44 offset-2 ${isPlayerInList ? "btn-danger" : "btn-success"
                                  }`}
                                onClick={() => handleAddRemoveClick(listId, pid, isPlayerInList)}
                              >
                                {isPlayerInList ? "Remove" : "Add"}
                              </button>
                            ) : (
                              <button className="btn btn-sm text-white col-44 offset-2 btn-secondary" disabled>
                                Disabled
                              </button>
                            )}
                          </div>

                          <div
                            className={`shareBox ${listId === String(activeListIDForShare ?? "") ? "selected" : ""}`}
                            onClick={() => handleShareClick(listId)}
                            title="Share/UnShare"
                          >
                            <img
                              src={String(listItem?.sharedToId ?? "") !== "" ? shareIcon : notShare}
                              alt="Checkbox Icon"
                              style={{ width: "24px", height: "24px" }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ---------------- Edit Content ---------------- */}
                <div id="editContent" style={{ display: isEditMode ? "block" : "none", height: "100%" }}>
                  <EditBox listID={theRecordToEdit?.listId} onClose={() => setIsEditMode(false)} />

                  <div className="row mt-3">
                    <div className="col-48 text-center">
                      <button className="btn btn-success text-white px-3" onClick={() => setIsEditMode(false)}>
                        Back to List
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right sidebar with header controls */}
        {isExpanded && activeListIDForShare && (
          <div className="rightSide">
            <div className="d-flex justify-content-end gap-2 ms-auto mb-2">
              <button
                className="btn btn-outline-primary btn-sm"
                style={{ minWidth: "30px", height: "32px", padding: "2px 12px", fontSize: "0.9rem" }}
                onClick={handleListLog}
              >
                List Log
              </button>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                style={{ minWidth: "30px", height: "32px", padding: "2px 12px", fontSize: "0.9rem" }}
                aria-label="Close"
                onClick={closeScoutDrawer}
              >
                X
              </button>
            </div>
            <ScoutBox checkBoxArray={sharedScoutList} displayName={displayNameForShare} onClick={processWhatScout} />
          </div>
        )}
      </div>

      {showLogModal && (
        <>
          <div
            className="modal-backdrop show"
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1040 }}
            onClick={() => setShowLogModal(false)}
          />
          <div className="modal show d-block littlebox-modal" tabIndex={-1} role="dialog" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h6 className="modal-title">List Log</h6>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowLogModal(false)} />
                </div>
                <div className="modal-body">
                  <LittleBoxLog
                    listId={activeListIDForShare}
                    listName={displayNameForShare}
                    onClose={() => setShowLogModal(false)}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LittleBox;
